// src/components/SlotActionMenu.jsx
// Small floating menu shown when user clicks an empty calendar slot.
// Offers "Block this time" (busy block) and "Create availability window" (open slot).
import { useEffect, useRef } from 'react';
import { Lock, Clock } from 'lucide-react';
import { format } from 'date-fns';
import styles from './SlotActionMenu.module.css';

/**
 * SlotActionMenu
 *
 * Props:
 *   slot       Date              — the clicked slot time
 *   anchor     { x, y }         — page coordinates for positioning
 *   onBlock    fn(slot)          — user chose "Block this time"
 *   onWindow   fn(slot)          — user chose "Create availability window"
 *   onClose    fn()              — dismiss without action
 */
export default function SlotActionMenu({ slot, anchor, onBlock, onWindow, onClose }) {
  const menuRef = useRef(null);

  // Close on outside click or Escape
  useEffect(() => {
    function handleKey(e) {
      if (e.key === 'Escape') onClose();
    }
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
    }
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [onClose]);

  // Keep menu inside viewport
  const style = {
    left: anchor.x,
    top: anchor.y,
  };

  const timeLabel = slot ? format(slot, 'h:mm a') : '';

  return (
    <div className={styles.menu} style={style} ref={menuRef} role="menu" aria-label="Slot actions">
      <div className={styles.timeHint}>{timeLabel}</div>
      <button
        className={styles.item}
        role="menuitem"
        onClick={() => { onBlock(slot); onClose(); }}
      >
        <Lock size={14} className={styles.itemIcon} aria-hidden="true" />
        Block this time
      </button>
      <button
        className={styles.item}
        role="menuitem"
        onClick={() => { onWindow(slot); onClose(); }}
      >
        <Clock size={14} className={styles.itemIcon} aria-hidden="true" />
        Create availability window
      </button>
    </div>
  );
}
